import React, { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import DashboardLayout from '../../components/Layout/DashboardLayout'
import Sidebar from '../../components/Sidebar/Sidebar'
import HospitalSelector from '../../components/Header/HospitalSelector'
import { TmbSliderProvider } from '../../context/TmbSliderContext'
import PatientSurveys from './PatientSurveys'
import PopulationCharacteristics from './PopulationCharacteristics'
import HuddleParticipation from './HuddleParticipation'
import AgreementSingle from './AgreementSingle'
import AgreementInsurance from './AgreementInsurance'
import AgreementEthnicity from './AgreementEthnicity'
import AgreementAcross from './AgreementAcross'
import AgreementEducation from './AgreementEducation'
import AgreementDelivery from './AgreementDelivery'
import AgreementComplications from './AgreementComplications'
import DisagreementSingle from './DisagreementSingle'
import DisagreementAcross from './DisagreementAcross'
import GeneralPatientComments from './GeneralPatientComments'
import TeamBirthComments from './TeamBirthComments'
import OutcomeMeasures from './OutcomeMeasures'
import PatientRecognition from './PatientRecognition'

const RedirectWithParams: React.FC<{ to: string }> = ({ to }) => {
    const location = useLocation()
    return <Navigate to={`${to}${location.search}`} replace />
}

const TMB_SIDEBAR_LINKS = [
    { label: 'Patient Surveys', path: '/tmb/patient-surveys/' },
    { label: 'Population Characteristics', path: '/tmb/population-characteristics/' },
    { label: 'Huddle Participation', path: '/tmb/huddle-participation/' },
    { label: 'Board Use', path: '/tmb/board-use/' },
    { label: 'TeamBirth Comments', path: '/tmb/teambirth-comments/' },
    { label: 'General Patient Comments', path: '/tmb/patient-comments/' },
    {
        label: 'Patient Agreement to Questions by Huddle',
        path: '',
        submenu: [
            { label: 'Single Question', path: '/tmb/agreement/single/' },
            { label: 'Across Questions', path: '/tmb/agreement/across/' },
            { label: 'Stratified by Race/Ethnicity', path: '/tmb/agreement/ethnicity/' },
            { label: 'Stratified by Insurance', path: '/tmb/agreement/insurance/' },
            { label: 'Stratified by Education', path: '/tmb/agreement/education/' },
            { label: 'Stratified by Delivery Method', path: '/tmb/agreement/delivery/' },
            { label: 'Stratified by Complications', path: '/tmb/agreement/complications/' },
        ],
    },
    {
        label: 'Patient Disagreement to Questions by Huddle',
        path: '',
        submenu: [
            { label: 'Single Question', path: '/tmb/disagreement/single/' },
            { label: 'Across Questions', path: '/tmb/disagreement/across/' },
        ],
    },
    { label: 'Outcome Measures', path: '/tmb/outcome-measures/' },
]

const TMB_EXPORT_PAGES = [
    { url: '/tmb/patient-surveys/', title: 'Patient Surveys' },
    { url: '/tmb/population-characteristics/', title: 'Population Characteristics' },
    { url: '/tmb/huddle-participation/', title: 'Huddle Participation' },
    { url: '/tmb/board-use/', title: 'Board Use' },
    { url: '/tmb/teambirth-comments/', title: 'TeamBirth Comments' },
    { url: '/tmb/patient-comments/', title: 'General Patient Comments' },
    { url: '/tmb/agreement/single/', title: 'Agreement: Single Question' },
    { url: '/tmb/agreement/across/', title: 'Agreement: Across Questions' },
    { url: '/tmb/agreement/ethnicity/', title: 'Agreement: Stratified by Race/Ethnicity' },
    { url: '/tmb/agreement/insurance/', title: 'Agreement: Stratified by Insurance' },
    { url: '/tmb/agreement/education/', title: 'Agreement: Stratified by Education' },
    { url: '/tmb/agreement/delivery/', title: 'Agreement: Stratified by Delivery Method' },
    { url: '/tmb/agreement/complications/', title: 'Agreement: Stratified by Complications' },
    { url: '/tmb/disagreement/single/', title: 'Disagreement: Single Question' },
    { url: '/tmb/disagreement/across/', title: 'Disagreement: Across Questions' },
    { url: '/tmb/outcome-measures/', title: 'Outcome Measures' },
]

const TMBIndex: React.FC = () => {
    useEffect(() => {
        const handler = (e: AnimationEvent) => {
            const el = e.target as HTMLElement
            if (e.animationName === 'barGrow') {
                el.style.animation = 'none'
                el.style.transform = 'scaleY(1)'
            } else if (e.animationName === 'fadeSlideIn') {
                el.style.animation = 'none'
                el.style.opacity = '1'
                el.style.transform = 'translateY(0)'
            }
        }
        document.addEventListener('animationend', handler)
        return () => document.removeEventListener('animationend', handler)
    }, [])

    useEffect(() => {
        const fontLinkId = 'tmb-poppins-font-link'
        const styleId = 'tmb-font-scope-style'

        let fontLink = document.getElementById(fontLinkId) as HTMLLinkElement | null
        if (!fontLink) {
            fontLink = document.createElement('link')
            fontLink.id = fontLinkId
            fontLink.rel = 'stylesheet'
            fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap'
            document.head.appendChild(fontLink)
        }

        let styleEl = document.getElementById(styleId) as HTMLStyleElement | null
        if (!styleEl) {
            styleEl = document.createElement('style')
            styleEl.id = styleId
            styleEl.textContent = `
.tmb-scope {
  font-family: "Poppins", "Segoe UI", sans-serif;
  width: 100%;
  min-height: 100vh;
}
.tmb-scope button,
.tmb-scope input,
.tmb-scope select,
.tmb-scope textarea {
  font-family: inherit;
}
.tmb-scope .apexcharts-canvas,
.tmb-scope .apexcharts-svg,
.tmb-scope .apexcharts-text,
.tmb-scope .apexcharts-title-text,
.tmb-scope .apexcharts-subtitle-text,
.tmb-scope .apexcharts-legend-text,
.tmb-scope .apexcharts-tooltip,
.tmb-scope .apexcharts-tooltip-title,
.tmb-scope .apexcharts-tooltip-text,
.tmb-scope .apexcharts-tooltip-y-group,
.tmb-scope .apexcharts-tooltip-text-y-label,
.tmb-scope .apexcharts-tooltip-text-y-value,
.tmb-scope .apexcharts-menu-item {
  font-family: "Poppins", "Segoe UI", sans-serif !important;
}
.tmb-scope .sidebar .tab,
.tmb-scope .sidebar .toggle-submenu {
  font-family: "Poppins", "Segoe UI", sans-serif !important;
}
            `.trim()
            document.head.appendChild(styleEl)
        }

        return () => {
            const s = document.getElementById(styleId)
            if (s) s.remove()
            const l = document.getElementById(fontLinkId)
            if (l) l.remove()
        }
    }, [])

    const sidebar = (
        <Sidebar
            logo="/logo1.webp"
            links={TMB_SIDEBAR_LINKS}
            exportPages={TMB_EXPORT_PAGES}
            exportTitle="TMB Designation Dashboard"
        />
    )

    const header = <HospitalSelector />

    return (
        <TmbSliderProvider>
        <div className="tmb-scope">
            <DashboardLayout sidebar={sidebar} header={header}>
                <Routes>
                    <Route path="/" element={<RedirectWithParams to="/tmb/patient-surveys/" />} />
                    <Route path="/patient-surveys/" element={<PatientSurveys />} />
                    <Route path="/population-characteristics/" element={<PopulationCharacteristics />} />
                    <Route path="/huddle-participation/" element={<HuddleParticipation />} />

                    <Route path="/board-use/" element={<PatientRecognition />} />
                    <Route path="/patient-recognition/" element={<RedirectWithParams to="/tmb/board-use/" />} />
                    <Route path="/teambirth-comments/" element={<TeamBirthComments />} />
                    <Route path="/board-use-comments/" element={<RedirectWithParams to="/tmb/teambirth-comments/" />} />
                    <Route path="/patient-comments/" element={<GeneralPatientComments />} />

                    <Route path="/agreement/single/" element={<AgreementSingle />} />
                    <Route path="/agreement/across/" element={<AgreementAcross />} />
                    <Route path="/agreement/ethnicity/" element={<AgreementEthnicity />} />
                    <Route path="/agreement/insurance/" element={<AgreementInsurance />} />
                    <Route path="/agreement/education/" element={<AgreementEducation />} />
                    <Route path="/agreement/delivery/" element={<AgreementDelivery />} />
                    <Route path="/agreement/complications/" element={<AgreementComplications />} />

                    <Route path="/disagreement/single/" element={<DisagreementSingle />} />
                    <Route path="/disagreement/across/" element={<DisagreementAcross />} />

                    <Route path="/outcome-measures/" element={<OutcomeMeasures />} />
                    <Route path="*" element={<RedirectWithParams to="/tmb/patient-surveys/" />} />
                </Routes>
            </DashboardLayout>
        </div>
        </TmbSliderProvider>
    )
}

export default TMBIndex
